import React, { useState } from 'react';

import TpDialog from '../shared/tp_dialog';
import { discard_guard_text } from '../shared/dialog_logic';
import { DialogFooter, ErrorBox, FieldError, NoteField, RowContext } from './dialog_parts';
import { rupees } from './adapters';
import { waive_form_errors } from './form_errors';
import { reason_options } from './params';

/*
 * PROD-7828 R6 — waive or un-waive one penalty (D81): a full waive of the row's current charge, its own
 * request and its own audit row. Partial relief is a correction (N1). Un-waiving puts the charge back
 * and keeps the row frozen against the engine; only Revert hands it back (N2). "Approved by" is a label
 * (D91). Props-only: the session page's Change Penalty modal mounts this same form.
 */
const WaiveForm = ({ row, waive, saving, error, on_submit, on_close, on_focus_lost }) => {
  const [form, setForm] = useState({ reason_code: '', note: '', approved_by: '' });
  const [touched, setTouched] = useState(false);
  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const errors = waive_form_errors(form, waive);
  const server = (error && error.fields) || {};
  const title = waive ? 'Waive penalty' : 'Undo the waiver';

  const submit = () => {
    setTouched(true);
    if (Object.values(errors).some(Boolean)) return;
    on_submit({ ...form, waive });
  };

  return (
    <TpDialog
      title_id="tp-pe-waive-title"
      busy={saving}
      guard_text={discard_guard_text(form.note, form.approved_by)}
      on_close={on_close}
      on_focus_lost={on_focus_lost}
    >
      <div className="tp-modal">
        <h4 id="tp-pe-waive-title">{title}</h4>
        <RowContext row={row} />
        <div className="tp-hint">
          {waive
            ? `Waiving removes the whole ${rupees(row.charged)} charge from ${row.teacher_name}'s month. To reduce it instead, use Correct.`
            : `The ${rupees(row.basis)} charge goes back on ${row.teacher_name}'s month. The penalty stays as Ops set it; use Revert to hand it back to the system.`}
        </div>
        <div className="tp-field">
          <label htmlFor="tp-pe-waive-reason">Reason</label>
          <select
            id="tp-pe-waive-reason"
            className="tp-select"
            value={form.reason_code}
            onChange={(e) => set('reason_code', e.target.value)}
          >
            <option value="">Choose a reason</option>
            {reason_options('waive').map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <FieldError text={(touched && errors.reason_code) || server.reason_code} />
        </div>
        {waive ? (
          <div className="tp-field">
            <label htmlFor="tp-pe-waive-approved">Approved by (optional)</label>
            <input
              id="tp-pe-waive-approved"
              className="tp-input"
              value={form.approved_by}
              placeholder="For example, Priya (Ops lead)"
              onChange={(e) => set('approved_by', e.target.value)}
            />
            <FieldError text={(touched && errors.approved_by) || server.approved_by} />
          </div>
        ) : null}
        <NoteField
          id="tp-pe-waive-note"
          label="Note (internal, required)"
          value={form.note}
          on_change={(v) => set('note', v)}
          error_text={(touched && errors.note) || server.note}
          placeholder={waive ? 'Why the penalty is waived' : 'Why the waiver is undone'}
        />
        <ErrorBox error={error} />
        <DialogFooter
          saving={saving}
          error={error}
          submit_label={waive ? 'Waive penalty' : 'Undo waiver'}
          on_submit={submit}
          on_confirm={submit}
          on_close={on_close}
        />
      </div>
    </TpDialog>
  );
};

export default WaiveForm;
